import { CreditCardOutlined, ThunderboltOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
} from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import useEmployee from '../../hook/useEmployee';
import useRole from '../../hook/useRole';
import CreditCardService from '../../service/credit-card-service/Credit-card.service';
import POSDeviceService, {
  OptimalPOSResponse,
} from '../../service/pos-device-service/POS-device.service';
import TransactionService from '../../service/transaction-service/Transaction.service';
import { formatCurrency } from '../withdrawal-check/utils';

const { Option } = Select;

interface CreateCardWithdrawalModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  creditCardId: number;
  cardInfo: {
    cardNumber: string;
    cardHolderName: string;
    customerName: string;
    bankName: string;
    cardType: string;
    defaultFeeRate?: number;
  };
}

const CreateCardWithdrawalModal: React.FC<CreateCardWithdrawalModalProps> = ({
  open,
  onClose,
  onSuccess,
  creditCardId,
  cardInfo,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [posDevices, setPosDevices] = useState<OptimalPOSResponse[]>([]);
  const [loadingPOS, setLoadingPOS] = useState(false);
  const [commissionRate, setCommissionRate] = useState<number>(0);
  const { employee } = useEmployee();
  const role = useRole();

  useEffect(() => {
    if (open) {
      // Reset form and load credit card details
      fetchCreditCardDetails();
      // Load POS devices for this card
      fetchPOSDevices();
    }
  }, [open, creditCardId]);

  const fetchCreditCardDetails = async () => {
    try {
      const creditCardData =
        await CreditCardService.getCreditCardById(creditCardId);

      // Set commission rate từ customer của credit card
      const customerCommission =
        creditCardData.customer?.commissionRateForPartner || 0;
      setCommissionRate(customerCommission);

      // Determine returnToCompany based on role
      const isAdmin = role === 'ROLE_ADMIN';

      // Reset form with data
      form.setFieldsValue({
        transactionDate: dayjs(),
        customerType: '%',
        status: 'PENDING',
        feeForCustomer: cardInfo.defaultFeeRate || 0,
        commissionRateForPartner: customerCommission,
        returnToCompany: isAdmin, // Auto set based on role, but field will be hidden
      });
    } catch (error) {
      console.error('Error fetching credit card details:', error);
      // Fallback form values
      form.setFieldsValue({
        transactionDate: dayjs(),
        customerType: '%',
        status: 'PENDING',
        feeForCustomer: cardInfo.defaultFeeRate || 0,
        commissionRateForPartner: 0,
        returnToCompany: role === 'ROLE_ADMIN',
      });
    }
  };

  const fetchPOSDevices = async () => {
    setLoadingPOS(true);
    try {
      const transactionDate =
        form.getFieldValue('transactionDate')?.format('YYYY-MM-DD') ||
        dayjs().format('YYYY-MM-DD');
      const response = await POSDeviceService.getOptimalPOSDevicesForCard(
        creditCardId,
        transactionDate
      );
      setPosDevices(response);
    } catch (error) {
      message.error('Không thể tải danh sách thiết bị POS');
      setPosDevices([]);
    } finally {
      setLoadingPOS(false);
    }
  };

  const handleDateChange = () => {
    // Reload POS devices when date changes
    setTimeout(() => {
      fetchPOSDevices();
    }, 100);
  };

  const handlePOSChange = (value: number) => {
    const selectedPOS = posDevices.find((device) => device.posId === value);
    if (selectedPOS) {
      form.setFieldsValue({ feeApplied: selectedPOS.feeRate });
    }
  };

  const calculateBalance = (amount: number, feeApplied: number) => {
    return amount - (amount * feeApplied) / 100;
  };

  const handleSubmit = async (values: any) => {
    if (!employee?.id) {
      message.error('Không thể xác định nhân viên hiện tại');
      return;
    }

    setLoading(true);
    try {
      // Auto determine returnToCompany based on role
      const isAdmin = role === 'ROLE_ADMIN';

      const requestData = {
        creditCardId: creditCardId,
        posDeviceId: values.posDeviceId,
        transactionDate: values.transactionDate.format('YYYY-MM-DD'),
        amount: values.amount,
        transactionType: 'withdrawal' as const,
        feeForCustomer: values.feeForCustomer || 0,
        customerType: values.customerType,
        feeApplied: values.feeApplied || 0,
        commissionRateForPartner: commissionRate, // Use commission rate from credit card's customer
        status: values.status,
        returnToCompany: isAdmin, // Auto set based on role
        transactionFor: 'withdrawal' as const,
      };

      const response =
        await TransactionService.createWithdrawalTransactionForCard(
          requestData,
          employee.id
        );

      if (response.success) {
        message.success('Tạo giao dịch rút tiền thành công');
        form.resetFields();
        onSuccess();
      } else {
        throw new Error(response.message || 'Có lỗi xảy ra khi tạo giao dịch');
      }
    } catch (error: any) {
      console.error('Error creating withdrawal transaction:', error);
      message.error(error.message || 'Có lỗi xảy ra khi tạo giao dịch');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const formatPOSDisplay = (device: OptimalPOSResponse) => {
    return `${device.deviceCode} - ${device.location} (${device.feeRate}%)`;
  };

  // Watch form values for balance calculation
  const amount = Form.useWatch('amount', form) || 0;
  const feeApplied = Form.useWatch('feeApplied', form) || 0;
  const balance = calculateBalance(amount, feeApplied);

  return (
    <Modal
      title={
        <div className="flex items-center space-x-2">
          <CreditCardOutlined className="text-blue-500" />
          <span>Tạo Giao Dịch Rút Tiền</span>
        </div>
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      {/* Card Info */}
      <Card size="small" className="mb-4 bg-blue-50">
        <div className="flex justify-between items-center">
          <div>
            <div className="font-semibold text-blue-600">
              ***{cardInfo.cardNumber.slice(-4)} - {cardInfo.cardHolderName}
            </div>
            <div className="text-sm text-gray-600">
              {cardInfo.customerName} • {cardInfo.bankName}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Loại thẻ</div>
            <div className="font-medium">{cardInfo.cardType}</div>
          </div>
        </div>
      </Card>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          transactionDate: dayjs(),
          customerType: '%',
          status: 'PENDING',
          commissionRateForPartner: 0,
        }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="transactionDate"
              label="Ngày giao dịch"
              rules={[
                { required: true, message: 'Vui lòng chọn ngày giao dịch' },
              ]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder="Chọn ngày giao dịch"
                onChange={handleDateChange}
                disabledDate={(current) => {
                  if (!current) return false;
                  const today = dayjs().startOf('day');
                  const min = today.subtract(10, 'day');
                  const max = today.add(10, 'day');
                  return current.isBefore(min, 'day') || current.isAfter(max, 'day');
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="amount"
              label="Số tiền rút"
              rules={[
                { required: true, message: 'Vui lòng nhập số tiền' },
                { type: 'number', min: 1, message: 'Số tiền phải lớn hơn 0' },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Nhập số tiền"
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="posDeviceId"
              label={
                <div className="flex items-center space-x-2">
                  <span>Thiết bị POS</span>
                  <ThunderboltOutlined className="text-orange-500" />
                </div>
              }
              rules={[
                { required: true, message: 'Vui lòng chọn thiết bị POS' },
              ]}
            >
              <Select
                placeholder="Chọn thiết bị POS"
                loading={loadingPOS}
                onChange={handlePOSChange}
                showSearch
                optionFilterProp="children"
              >
                {posDevices.map((device) => (
                  <Option key={device.posId} value={device.posId}>
                    {formatPOSDisplay(device)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="feeApplied"
              label="Phí POS (%)"
              rules={[{ type: 'number', min: 0, message: 'Phí không được âm' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Phí POS tự động"
                min={0}
                max={100}
                step={0.01}
                readOnly
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider>Phí khách hàng</Divider>

        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item name="customerType" label="Loại phí khách hàng">
              <Select>
                <Option value="%">Phần trăm (%)</Option>
                <Option value="vn">Tiền mặt (VND)</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              name="feeForCustomer"
              label="Phí khách hàng"
              rules={[{ type: 'number', min: 0, message: 'Phí không được âm' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Nhập phí khách hàng"
                min={0}
                step={0.01}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item name="commissionRateForPartner" label="Phí CTV (%)">
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Phí CTV từ thông tin khách hàng"
                min={0}
                max={100}
                step={0.01}
                value={commissionRate}
                readOnly
                className="bg-gray-50"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="status" label="Trạng thái">
              <Select>
                <Option value="PENDING">Đang chờ</Option>
                <Option value="COMPLETED">Hoàn thành</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <div className="text-sm text-gray-600 pt-6">
              <p>
                <strong>Luồng tiền:</strong>{' '}
                {role === 'ROLE_ADMIN' ? (
                  <span className="text-blue-600">Về tài khoản công ty</span>
                ) : (
                  <span className="text-green-600">Về tài khoản nhân viên</span>
                )}
              </p>
            </div>
          </Col>
        </Row>

        {/* Balance Display */}
        {amount > 0 && (
          <Card size="small" className="mb-4 bg-green-50">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Thực nhận sau phí POS:</span>
              <span className="text-lg font-semibold text-green-600">
                {formatCurrency(balance)}
              </span>
            </div>
          </Card>
        )}

        <Form.Item className="mb-0 mt-4">
          <div className="flex justify-end space-x-2">
            <Button onClick={handleCancel}>Hủy</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Tạo giao dịch
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateCardWithdrawalModal;
